/**
 * NexaStay booking engine API (public, no auth).
 */
import api from '../services/api';

const BASE = '/nexastay';

export async function getHotelInfo(hotelId) {
  const { data } = await api.get(`${BASE}/api/hotel/${hotelId}/`);
  return data;
}

export async function getHotelRoomTypes(hotelId) {
  const { data } = await api.get(`${BASE}/api/hotel/${hotelId}/room-types/`);
  return data;
}

export async function getRoomTypesWithAvailability(hotelId, checkIn, checkOut, ratePlanId = null, allRatePlans = false) {
  const params = { check_in: checkIn, check_out: checkOut };
  if (ratePlanId) params.rate_plan_id = ratePlanId;
  if (allRatePlans) params.all_rate_plans = '1';
  const { data } = await api.get(`${BASE}/api/hotel/${hotelId}/room-types-availability/`, {
    params,
  });
  return data;
}

export async function getChannelsPrices(hotelId, date) {
  const { data } = await api.get(`${BASE}/api/hotel/${hotelId}/channels-prices/`, {
    params: date ? { date } : {},
  });
  return data;
}

export async function getHotelAmenities(hotelId) {
  const { data } = await api.get(`${BASE}/api/hotel/${hotelId}/amenities/`);
  return data;
}

/** Get rate plans (meal plans) for the hotel from configuration. */
export async function getHotelRatePlans(hotelId) {
  const { data } = await api.get(`${BASE}/api/hotel/${hotelId}/rate-plans/`);
  return data;
}

/** Register guest (create in Guest table). Returns { guest_id, guest }. */
export async function registerGuest(hotelId, payload) {
  const { data } = await api.post(`${BASE}/api/hotel/${hotelId}/register/`, payload);
  return data;
}

/** Guest login by email or phone + password. Returns { guest_id, guest }. */
export async function loginGuest(hotelId, { email_or_phone, password }) {
  const { data } = await api.post(`${BASE}/api/hotel/${hotelId}/login/`, {
    email_or_phone: email_or_phone?.trim?.() ?? '',
    password: password ?? '',
  });
  return data;
}

/** Guest login/signup with Google ID token. Returns { guest_id, guest }. */
export async function loginWithGoogle(hotelId, idToken) {
  const { data } = await api.post(`${BASE}/api/hotel/${hotelId}/auth/google/`, {
    id_token: idToken?.trim?.() ?? '',
  });
  return data;
}

/** Get guest profile for profile page. */
export async function getGuestProfile(hotelId, guestId) {
  const { data } = await api.get(`${BASE}/api/hotel/${hotelId}/guest/${guestId}/`);
  return data;
}

/** Update guest profile. Payload: first_name, last_name, email, phone, address_line1, city, state, country, pincode, new_password (optional). */
export async function updateGuest(hotelId, guestId, payload) {
  const { data } = await api.patch(`${BASE}/api/hotel/${hotelId}/guest/${guestId}/`, payload);
  return data;
}

/** Get bookings for the logged-in guest at this hotel. */
export async function getGuestBookings(hotelId, guestId) {
  const { data } = await api.get(`${BASE}/api/hotel/${hotelId}/guest/${guestId}/bookings/`);
  return data;
}

/** Cancel a booking. Only the authorized guest (owner of the booking) can cancel. */
export async function cancelGuestBooking(hotelId, guestId, bookingId) {
  const { data } = await api.post(
    `${BASE}/api/hotel/${hotelId}/guest/${guestId}/bookings/${bookingId}/cancel/`
  );
  return data;
}

/** Create booking (guest details + rooms). Returns { booking_id, booking_code }. */
export async function createBooking(hotelId, payload) {
  const idempotencyKey = payload?.idempotencyKey || payload?.idempotency_key;
  const config = idempotencyKey
    ? { headers: { 'X-Idempotency-Key': idempotencyKey } }
    : undefined;
  const { data } = await api.post(`${BASE}/api/hotel/${hotelId}/book/`, payload, config);
  return data;
}

/** Initiate Razorpay order for partial/full NexaStay checkout. */
export async function initiateNexaStayPayment(hotelId, payload) {
  const idempotencyKey = payload?.idempotencyKey || payload?.idempotency_key;
  const config = idempotencyKey
    ? { headers: { 'X-Idempotency-Key': idempotencyKey } }
    : undefined;
  const { data } = await api.post(`${BASE}/api/hotel/${hotelId}/payment/initiate/`, payload, config);
  return data;
}

/** Verify Razorpay payment and create booking. */
export async function verifyNexaStayPayment(hotelId, payload) {
  const { data } = await api.post(`${BASE}/api/hotel/${hotelId}/payment/verify/`, payload);
  return data;
}

/** Poll payment/booking confirmation status after Razorpay checkout. */
export async function getNexaStayPaymentStatus(hotelId, razorpayOrderId) {
  const { data } = await api.get(`${BASE}/api/hotel/${hotelId}/payment/status/`, {
    params: { razorpay_order_id: razorpayOrderId },
  });
  return data;
}

/** Get hotel terms & policies (from configuration/terms-and-policy). Returns { policies: [{ policy_type, policy_type_display, title, content, summary, version }] }. */
export async function getHotelPolicies(hotelId) {
  const { data } = await api.get(`${BASE}/api/hotel/${hotelId}/policies/`);
  return data;
}

/** Validate a NexaStay coupon code for hotel checkout. */
export async function validateNexaStayCoupon(hotelId, couponCode) {
  const { data } = await api.post(`${BASE}/api/hotel/${hotelId}/coupons/validate/`, {
    couponCode: couponCode?.trim?.() ?? '',
  });
  return data;
}

/** LocalStorage key for current guest id (per hotel). */
export function getGuestStorageKey(hotelId) {
  return `nexastay_guest_${hotelId}`;
}
